/**
 * The SessionStore seam (IoC) — the ONLY coupling between the grading elements
 * and persistence. The host app provides an implementation via the
 * {@link RlhfSessionStoreToken} context token; elements NEVER persist — they
 * append review events into the store and read its folded state signal.
 *
 * A default in-memory implementation ({@link createRlhfSessionStore}) folds the
 * log locally and exposes an `onAppend` hook the app wires to its own persistence
 * (e.g. POSTing the event log to a BFF). Every appended event is VALIDATED with
 * the rlhf-core `SessionEvent` union (validate-never-cast) before it lands.
 */

import { createToken, signal, type ReadonlySignal } from "@platform/web-kit"
import { type } from "arktype"
import { SessionEvent, sessionState, type SessionState } from "@platform/rlhf-core"

/** What the grading elements need from their host: append an event, read the fold. */
export interface RlhfSessionStore {
  /** Append one review event (the host persists; the store re-folds). */
  append(event: SessionEvent): void
  /** The folded session state as a live signal (elements render from it). */
  state(): ReadonlySignal<SessionState>
}

/** The IoC token the host provides the SessionStore under. */
export const RlhfSessionStoreToken = createToken<RlhfSessionStore>("rlhf-session-store")

export interface RlhfSessionStoreOptions {
  /** Seed events (e.g. an existing persisted log being resumed). */
  initial?: readonly SessionEvent[]
  /** Called after each validated append with the event + the full log (persist here). */
  onAppend?: (event: SessionEvent, log: readonly SessionEvent[]) => void
}

/** The in-memory store plus log accessors — the host's default implementation. */
export interface RlhfSessionStoreImpl extends RlhfSessionStore {
  /** The full append-only event log (for export / persistence). */
  events(): readonly SessionEvent[]
  /**
   * REHYDRATION: replace the log with a validated, persisted event log and re-fold
   * the state signal. Unlike {@link RlhfSessionStore.append} this does NOT invoke
   * `onAppend` — seeding from storage must not echo the whole log straight back to
   * the persistence sink. Every seed event is validated (validate-never-cast); an
   * invalid one throws and leaves the store untouched (all-or-nothing). Call this
   * BEFORE the grading elements connect so their `attach()` prefill reads the
   * restored state (see career-bench-grading's rehydration on surface load).
   */
  hydrate(events: readonly SessionEvent[]): void
}

/**
 * Create a local, folding SessionStore. Validates every append, re-folds the
 * state signal, and invokes `onAppend` so the host can persist the log.
 */
export function createRlhfSessionStore(options: RlhfSessionStoreOptions = {}): RlhfSessionStoreImpl {
  let log: SessionEvent[] = [...(options.initial ?? [])]
  const state = signal<SessionState>(sessionState(log))

  return {
    append(event) {
      const parsed = SessionEvent(event)
      if (parsed instanceof type.errors) {
        throw new Error(`[rlhf] refusing to append an invalid event: ${parsed.summary}`)
      }
      log.push(parsed)
      state.set(sessionState(log))
      options.onAppend?.(parsed, log)
    },
    hydrate(events) {
      // Validate the WHOLE seed before touching the log (all-or-nothing) so a corrupt
      // persisted event never half-replaces a good in-memory log.
      const seed: SessionEvent[] = []
      for (const event of events) {
        const parsed = SessionEvent(event)
        if (parsed instanceof type.errors) {
          throw new Error(`[rlhf] refusing to hydrate an invalid event: ${parsed.summary}`)
        }
        seed.push(parsed)
      }
      log = seed
      state.set(sessionState(log))
    },
    state() {
      return state
    },
    events() {
      return log
    },
  }
}
